import { useId } from "react";
import type { ReactElement } from "react";
import type { LayerVisibility } from "../../state/layerState";
import type { InspectorPresentation } from "./BaseLayersCard";
import {
  InspectorChipButton,
  InspectorChipButtonRow
} from "./InspectorChipButton";
import { InspectorRow } from "./InspectorRow";
import { Spacer } from "../Spacer";

export const FLOOD_VISIBILITY_KEYS = [
  "floodOverlay",
  "wetlandsOverlay",
  "hydrographyOverlay",
  "seaLevelRiseOverlay",
  "stormSurgeOverlay"
] as const;

export type FloodVisibilityKey = (typeof FLOOD_VISIBILITY_KEYS)[number];

const LABELS: Record<FloodVisibilityKey, string> = {
  floodOverlay: "FEMA Flood zones",
  wetlandsOverlay: "Wetlands (NWI)",
  hydrographyOverlay: "Streams & ponds (NHD)",
  seaLevelRiseOverlay: "Sea level rise (3 ft)",
  stormSurgeOverlay: "Storm surge (Cat 3)"
};

export interface FloodLayersCardProps {
  visibility: LayerVisibility;
  onToggle: (key: FloodVisibilityKey, value: boolean) => void;
  onRefreshExternalData: () => void;
  presentation?: InspectorPresentation;
  /** Visibility keys whose value differs from the viewer default (accent dot). */
  modifiedKeys?: ReadonlySet<string>;
}

export function FloodLayersCard({
  visibility,
  onToggle,
  onRefreshExternalData,
  presentation = "inspector",
  modifiedKeys
}: FloodLayersCardProps): ReactElement {
  const reactId = useId();

  return (
    <div>
      {FLOOD_VISIBILITY_KEYS.map((key) => (
        <InspectorRow
          key={key}
          label={LABELS[key]}
          labelWeight="normal"
          checkboxId={`${reactId}-flood-${key}`}
          checked={visibility[key]}
          modified={modifiedKeys?.has(key) === true}
          onCheckedChange={(v) => {
            onToggle(key, v);
          }}
        />
      ))}
      <Spacer size="xs" noBorder />
      <InspectorChipButtonRow>
        <InspectorChipButton
          drawerRail={presentation === "drawer"}
          onClick={() => {
            onRefreshExternalData();
          }}
        >
          Refresh external data
        </InspectorChipButton>
      </InspectorChipButtonRow>
    </div>
  );
}
